import { createSelector, OutputParametricSelector } from 'reselect';
import { pathOr } from 'ramda';

import { SecurityState } from './types';
import { SECURITY_USER_PROFILE_DEFAULT, SECURITY_INIT_OPTIONS_DEFAULT } from './defaults';

const getSecurity = (state: { security: SecurityState }): SecurityState => state.security;

export const selectIsLoading = createSelector([getSecurity], ({ isLoading = true }) => isLoading);

export const selectIsFailed = createSelector([getSecurity], ({ isFailed = false }) => isFailed);

export const selectIsLogged = createSelector([getSecurity], ({ isLogged = false }) => isLogged);

export const selectAuthToken = createSelector([getSecurity], ({ token }) => token);

export const selectUser = createSelector(
    [getSecurity],
    ({ user = SECURITY_USER_PROFILE_DEFAULT }) => user
);

export const selectSecurityOptions = createSelector(
    [getSecurity],
    ({ options = SECURITY_INIT_OPTIONS_DEFAULT }) => options
);

export const selectIsLoginFromIFrameEnabled = createSelector(
    [selectSecurityOptions],
    ({ loginFromIFrame = false }) => loginFromIFrame
);

export const selectIsLoginRequired = createSelector(
    [selectSecurityOptions],
    ({ loginRequired = false }) => loginRequired
);

export const selectUsername = createSelector([selectUser], ({ username }) => username);

export const selectUserGroups = createSelector(selectUser, ({ groups = [] }) => groups);

export const selectUserAttributes = createSelector(selectUser, ({ attributes }) => attributes);

export const selectUserFeatureFlags = createSelector(selectUser, ({ featureFlags }) => featureFlags);

export const selectIsFinarfin = createSelector(
    selectUserAttributes,
    (attributes) =>
        (pathOr('false', ['ff.console.finarfin', 0], attributes) as string) === ('true' as string)
);

export const selectUserTenant = createSelector(selectUserAttributes, ({ tenant = [] }) => tenant[0]);

export const selectUserFullName = createSelector(
    [selectUser],
    ({ firstName, lastName }) => `${firstName} ${lastName}`
);

export const selectAccountUrl = createSelector([getSecurity], ({ accountUrl = '' }) => accountUrl);

export const selectUserRoles = createSelector([getSecurity], ({ roles = [] }) => roles);

export const makeSelectIsRoleIncluded = (): OutputParametricSelector<
    any,
    { includedRole: string },
    boolean,
    (res1: string[], res2: string) => boolean
> =>
    createSelector([selectUserRoles, (_, { includedRole }) => includedRole], (roles, includedRole) =>
        roles.some((role) => role === includedRole)
    );
